import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { passwordCopy, passwordSchema } from '@nesso/ui/lib/password.schema';
import { AuthPasswordField } from '@nesso/ui/primitives/auth-password-field';
import { Button } from '@nesso/ui/primitives/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@nesso/ui/primitives/dialog';

export type SecurityPasswordSubmit = {
    currentPassword?: string;
    newPassword: string;
};

export type SecurityPasswordFieldError = {
    field: 'currentPassword' | 'newPassword';
    message: string;
};

const dialogFormSchema = z.object({
    currentPassword: z.string(),
    newPassword: passwordSchema,
});

type FormValues = z.input<typeof dialogFormSchema>;

type Props = {
    open: boolean;
    hasCurrentPassword: boolean;
    pending?: boolean;
    fieldError?: SecurityPasswordFieldError;
    onSubmit: (values: SecurityPasswordSubmit) => void;
    onCancel: () => void;
};

export function SecurityPasswordDialog({
    open,
    hasCurrentPassword,
    pending,
    fieldError,
    onSubmit,
    onCancel,
}: Props) {
    const schema = z.object({
        currentPassword: z.string(),
        newPassword: passwordSchema,
    });

    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: { currentPassword: '', newPassword: '' },
        mode: 'onChange',
    });

    useEffect(() => {
        if (open) form.reset({ currentPassword: '', newPassword: '' });
    }, [open, form]);

    useEffect(() => {
        if (fieldError) form.setError(fieldError.field, { message: fieldError.message });
    }, [fieldError, form]);

    const submit = form.handleSubmit((values) => {
        if (pending) return;
        onSubmit(
            hasCurrentPassword
                ? { currentPassword: values.currentPassword, newPassword: values.newPassword }
                : { newPassword: values.newPassword },
        );
    });

    return (
        <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
            <DialogContent>
                <DialogHeader className="text-left">
                    <DialogTitle>
                        {hasCurrentPassword
                            ? 'Confirm your identity to update password'
                            : 'Set new password'}
                    </DialogTitle>
                    <DialogDescription>
                        Choose a strong password that you don&rsquo;t use for other accounts
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="flex flex-col gap-6">
                    {hasCurrentPassword && (
                        <Controller
                            control={form.control}
                            name="currentPassword"
                            render={({ field, fieldState }) => {
                                const showError =
                                    fieldState.isTouched || form.formState.submitCount > 0;
                                return (
                                    <AuthPasswordField
                                        ref={field.ref}
                                        id="current-password"
                                        label="Current password"
                                        autoComplete="current-password"
                                        value={field.value}
                                        disabled={pending}
                                        error={showError ? fieldState.error?.message : undefined}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                    />
                                );
                            }}
                        />
                    )}

                    <Controller
                        control={form.control}
                        name="newPassword"
                        render={({ field, fieldState }) => {
                            const showError =
                                fieldState.isTouched || form.formState.submitCount > 0;
                            return (
                                <AuthPasswordField
                                    ref={field.ref}
                                    id="new-password"
                                    label="New password"
                                    autoComplete="new-password"
                                    value={field.value}
                                    disabled={pending}
                                    error={showError ? fieldState.error?.message : undefined}
                                    description={passwordCopy.complexity}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                />
                            );
                        }}
                    />

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onCancel}
                            disabled={pending}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={!form.formState.isValid || pending}>
                            Continue
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
